import uuid from 'uuid/v4';

import { isFunction } from '@philips-emr/tws-utils';

import { Value } from '../../types';
import authenticationService from '../auth/authentication-service';
import HttpService, { HttpHeader, HttpMethod, HttpStatus, MediaType, RequestOptions, RequestResponse, ResponseBodyMapperType } from '../http-service';

import FileHandlerConfiguration, { FileHandler, FileInterface, RemoveCallback } from './file-handler-configuration';

function applyAcceptHeader(request: XMLHttpRequest): void {
  request.setRequestHeader(HttpHeader.ACCEPT, MediaType.APPLICATION_JSON);
}

function applyAuthorizationHeader(request: XMLHttpRequest): void  {
  const token = authenticationService.getJWTToken();

  if (token) {
    request.setRequestHeader(HttpHeader.AUTHORIZATION, `${token.getType()} ${token.rawToken()}`);
  }
}

function getFileInterface(fileName: string, request: XMLHttpRequest, state: string, percentage: number, path?: string): FileInterface {
  const uploadProgress = { fileName, path, percentage, state };

  const onCancel = (): void => {
    if (request.readyState === XMLHttpRequest.UNSENT) {
      return;
    }

    request.abort();
  };

  return {
    onCancel,
    uploadProgress,
  };
}

export interface EventsRequest {
  onProgress: Function;
}

export default class DefaultFileHandler implements FileHandler {
  private fileHandlerConfiguration: FileHandlerConfiguration;

  constructor() {
    this.fileHandlerConfiguration = new FileHandlerConfiguration();
  }

  download(endpoint: string, name?: string, parameters: Record<string, Value> = {}, method: HttpMethod = HttpMethod.GET, options: RequestOptions = {}): Promise<Blob> {
    options.responseBodyMapperType = options.responseBodyMapperType || ResponseBodyMapperType.BLOB;
    return HttpService.fetch<Blob>(endpoint, method, parameters, options)
      .then((response: RequestResponse<Blob>) => {
        const anchor = document.createElement('a');
        const filename = name || uuid();

        anchor.download = filename;
        anchor.href = URL.createObjectURL(response.body);
        anchor.click();

        URL.revokeObjectURL(anchor.href);

        return response.body || new Blob();
      });
  }

  preview(path: string): void {
    const { previewUrl } = this.fileHandlerConfiguration;

    window.open(previewUrl + path);
  }

  upload(file: File, events: EventsRequest): Promise<FileInterface> {
    return new Promise<FileInterface>((resolve, reject) => {

      const request = new XMLHttpRequest();

      request.upload.addEventListener('abort', () => this.abort(events, file, request));
      request.upload.addEventListener('error', () => this.error(reject, file, request));
      request.upload.addEventListener('load', () => this.load(events, file, request));
      request.upload.addEventListener('loadend', () => this.loadEnd(resolve, reject, file, request));
      request.upload.addEventListener('progress', (event) => this.progress(event, events, file, request));

      const formData = new FormData();
      formData.append('file', file, file.name);

      const { uploadUrl } = this.fileHandlerConfiguration;

      request.open('POST', uploadUrl, true);

      applyAcceptHeader(request);
      applyAuthorizationHeader(request);

      request.send(formData);

      request.onreadystatechange = function() {
        if (request.status === HttpStatus.UNAUTHORIZED) {
          authenticationService.requestRefreshToken().then(() => {
            applyAuthorizationHeader(request);
          });
        }
      };
    });
  }

  progress = (event: ProgressEvent, events: EventsRequest, file: File, req: XMLHttpRequest): void => {
    if (!event.lengthComputable) {
      return;
    }

    const percentage = (event.loaded / event.total) * 100;
    const fileInterface = getFileInterface(file.name, req, 'pending', percentage);
    const { onProgress } = events;

    onProgress(fileInterface);
  };

  abort = (events: EventsRequest, file: File, req: XMLHttpRequest): void => {
    const fileInterface = getFileInterface(file.name, req, 'aborted', 0);
    const { onProgress } = events;

    onProgress(fileInterface);
  };

  error = (reject: Function, file: File, req: XMLHttpRequest): void => {
    const fileInterface = getFileInterface(file.name, req, 'error', 0);
    reject(fileInterface);
  };

  loadEnd = (resolve: Function, reject: Function, file: File, req: XMLHttpRequest): void => {
    const { uploadResponseState } = this.fileHandlerConfiguration;

    req.onreadystatechange = function() {
      if (req.readyState != XMLHttpRequest.DONE || req.status != uploadResponseState) {
        return;
      }
      try {
        const path = JSON.parse(req.response).path;
        resolve(getFileInterface(file.name, req, 'done', 100, path));
      } catch (e) {
        reject(getFileInterface(file.name, req, 'error', 0));
      }
    };
  };

  load = (events: EventsRequest, file: File, req: XMLHttpRequest): void => {
    const fileInterface = getFileInterface(file.name, req, 'load', 100);
    const { onProgress } = events;

    onProgress(fileInterface);
  };

  remove(path: string, name: string, onExecuteRemove: RemoveCallback): void {
    const request = new XMLHttpRequest();
    const { removeResponseState, removeUrl } = this.fileHandlerConfiguration;
    const formData = new FormData();
    formData.set('path', path);

    request.open('DELETE', removeUrl, true);

    applyAcceptHeader(request);
    applyAuthorizationHeader(request);

    request.send(formData);

    request.onreadystatechange = function() {
      if (request.readyState != XMLHttpRequest.DONE) {
        return;
      }

      if (request.status === HttpStatus.UNAUTHORIZED) {
        authenticationService.requestRefreshToken().then(() => {
          applyAuthorizationHeader(request);
        });
      }

      if (isFunction(onExecuteRemove)) {
        onExecuteRemove(name, path, request.status, (request.status === removeResponseState));
      }
    };
  }

  setConfiguration(fileHandlerConfiguration: FileHandlerConfiguration): void {
    this.fileHandlerConfiguration = fileHandlerConfiguration;
  }
}
